import { CotacaoItensLote, CotacaoListItem } from '../../interfaces/PortalCotacoes';

/**
 * Cadeia de uma solicitação (12/08/2026).
 *
 * Um orçamento do Cilia gera uma solicitação por versão: `SOL-9100-v1` (inicial),
 * `SOL-9100-v3`, `SOL-9100-v4` (complementos). Todas pertencem à **mesma cadeia** — o
 * mesmo veículo, o mesmo orçamento — e é isso que permite avisar o comprador que ele está
 * cotando algo que já foi cotado, ou que ficou uma versão anterior sem cotar.
 *
 * ⚠️ Derivação em UM lugar só: quando a rota do Nasajon existir, o número passa a ser o do
 * ERP e a regra de cadeia muda aqui — não em cada tela. Se o formato não tiver `-vN`, a
 * cadeia é o próprio número (uma cadeia de um), e nenhum aviso falso aparece.
 */
export function cadeiaDaSolicitacao(numero: string): string {
  return numero.replace(/-v\d+$/i, '');
}

/** Cotação que ainda vale como "já cotado" — cancelada não conta. */
function contaComoCotada(cotacao: CotacaoListItem): boolean {
  return cotacao.status !== 'CANCELADA';
}

/**
 * Cotação em andamento: ainda pode virar pedido. É o recorte que importa para avisar sobre
 * peça removida por um complemento (item 20) — em cotação concluída o pedido já existe e
 * quem resolve é o ERP, não o Portal.
 */
export function estaEmAndamento(cotacao: { status: CotacaoListItem['status'] }): boolean {
  return cotacao.status !== 'CANCELADA' && cotacao.status !== 'CONCLUIDA';
}

/**
 * As cotações não canceladas de uma requisição específica. Vazio = requisição nunca cotada.
 *
 * ⚠️ Compara pelo `solicitacaoNumero` (o código PURO da requisição), nunca pelo `numero` da
 * cotação: desde a at-11 os dois são quase iguais, mas a cotação ganha sufixo (`-2`) quando a
 * requisição é cotada de novo, e comparar pelo número faria a recotação parecer de outra
 * requisição.
 */
export function cotacoesDaSolicitacao(
  numeroSolicitacao: string,
  cotacoes: CotacaoListItem[],
): CotacaoListItem[] {
  return cotacoes.filter((c) => contaComoCotada(c) && c.solicitacaoNumero === numeroSolicitacao);
}

/** As cotações não canceladas de toda a cadeia (todas as versões do mesmo orçamento). */
export function cotacoesDaCadeia(numeroSolicitacao: string, cotacoes: CotacaoListItem[]): CotacaoListItem[] {
  const cadeia = cadeiaDaSolicitacao(numeroSolicitacao);
  return cotacoes.filter(
    (c) => contaComoCotada(c) && cadeiaDaSolicitacao(c.solicitacaoNumero) === cadeia,
  );
}

/**
 * Versões da MESMA cadeia que estão aprovadas e nunca foram cotadas — exceto a que o
 * usuário acabou de escolher. É o aviso "a SOL-…-v1 desta cadeia ainda não foi cotada".
 *
 * ⚠️ Faz ainda mais sentido desde a at-11 (uma requisição por cotação, 28/09/2026): como
 * juntar versões deixou de existir, esquecer uma versão significa peça sem cotar, e a única
 * saída é criar a cotação dela.
 */
export function versoesNaoCotadasDaCadeia(
  numeroEscolhido: string | null,
  numerosAprovados: string[],
  cotacoes: CotacaoListItem[],
): string[] {
  if (!numeroEscolhido) return [];
  const cadeia = cadeiaDaSolicitacao(numeroEscolhido);
  return numerosAprovados.filter(
    (numero) =>
      numero !== numeroEscolhido &&
      cadeiaDaSolicitacao(numero) === cadeia &&
      cotacoesDaSolicitacao(numero, cotacoes).length === 0,
  );
}

/*
 * ⚠️ `mesclarItensDeSolicitacoes` e `ItemMesclado` foram REMOVIDOS na at-11 (28/09/2026):
 * eles somavam a mesma peça vinda de duas versões do orçamento numa linha só, e existiam
 * porque uma cotação podia juntar N solicitações. Decisão do Victor: uma cotação atende UMA
 * requisição — *"o correto é somente iniciar um processo por requisição, sem mesclar
 * processos"*. Quem cotar inicial e complemento cria duas cotações, cada uma com o número do
 * seu processo no Nasajon.
 */

// ── Cruzamento item x cotação (item 22 do backlog, 13/08/2026) ────────────────

export interface CotacaoDoItem {
  numero: string;
  status: CotacaoListItem['status'];
}

/**
 * Para cada `codigoProduto`, em quais cotações da cadeia ele JÁ está.
 *
 * É o que troca o aviso de cadeia ("este orçamento já tem cotação em outra versão") pelo
 * aviso por peça ("PARA-CHOQUE já está na COT-2026-0003"). Antes de existir a rota de itens
 * em lote, a listagem só trazia `totalItens` e este cruzamento era impossível.
 *
 * Cotação cancelada é ignorada — não está comprando nada.
 */
export function itensJaCotadosNaCadeia(
  lotes: CotacaoItensLote[],
): Map<string, CotacaoDoItem[]> {
  const porCodigo = new Map<string, CotacaoDoItem[]>();
  for (const lote of lotes) {
    if (lote.status === 'CANCELADA') continue;
    for (const item of lote.itens) {
      const atual = porCodigo.get(item.codigoProduto) ?? [];
      if (!atual.some((c) => c.numero === lote.numero)) {
        atual.push({ numero: lote.numero, status: lote.status });
      }
      porCodigo.set(item.codigoProduto, atual);
    }
  }
  return porCodigo;
}
